
export function ExperienceSection() {
  return (
    <section className="relative py-16 lg:py-32 bg-black overflow-hidden">
      {/* Background Dots */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
          backgroundSize: '30px 30px',
        }}
      />

      <div className="container mx-auto px-4 sm:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-10 lg:mb-20">
          <p className="text-gray-500 text-xs lg:text-sm tracking-[0.3em] mb-3 lg:mb-4 uppercase">
            How It Works
          </p>
          <h2 className="font-display text-3xl sm:text-5xl lg:text-8xl text-white mb-4 lg:mb-8 tracking-wider">
            <span className="inline-flex items-center flex-wrap justify-center gap-2 lg:gap-0">
              衝突
              <i className="ri-arrow-right-line text-xl sm:text-3xl lg:text-6xl text-neon-pink mx-1 lg:mx-6"></i>
              発火
              <i className="ri-arrow-right-line text-xl sm:text-3xl lg:text-6xl text-neon-green mx-1 lg:mx-6"></i>
              深度
            </span>
          </h2>
          <p className="text-gray-400 text-xs lg:text-xl max-w-4xl mx-auto leading-relaxed">
            物理的な衝撃を、デジタルな浮遊感へ。<br />
            3つのステップで、街の音楽を解放する。
          </p>
        </div>

        {/* Experience Steps */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-12 max-w-7xl mx-auto">
          {/* Step 1: Crash */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-b from-neon-pink/20 to-transparent rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-700" />
            <div className="relative bg-black/50 backdrop-blur-sm rounded-2xl lg:rounded-3xl p-6 lg:p-12 border border-white/10 hover:border-neon-pink/50 transition-all duration-500 h-full">
              {/* Step Number */}
              <div
                className="absolute -top-4 -left-4 lg:-top-6 lg:-left-6 w-10 h-10 lg:w-16 lg:h-16 rounded-full bg-neon-pink flex items-center justify-center font-display text-xl lg:text-3xl text-black"
                style={{ boxShadow: '0 0 40px rgba(255, 0, 110, 0.6)' }}
              >
                01
              </div>

              {/* Icon */}
              <div
                className="w-14 h-14 lg:w-20 lg:h-20 mb-4 lg:mb-8 rounded-xl lg:rounded-2xl bg-gradient-to-br from-neon-pink/20 to-transparent border-2 border-neon-pink flex items-center justify-center mx-auto"
                style={{ boxShadow: '0 0 30px rgba(255, 0, 110, 0.3)' }}
              >
                <i className="ri-smartphone-line text-2xl lg:text-4xl text-neon-pink"></i>
              </div>

              <h3 className="text-white text-xl lg:text-3xl font-bold mb-2 lg:mb-4 text-center">
                衝突 / CRASH
              </h3>

              <div
                className="w-2/5 h-0.5 bg-neon-pink mx-auto mb-4 lg:mb-8"
                style={{ boxShadow: '0 0 15px #ff006e' }}
              />

              <p className="text-gray-300 text-xs lg:text-base leading-relaxed text-center mb-4 lg:mb-6">
                街中の「ざらついた小物」にスマホを当てる。物理的な振動が指に伝わる瞬間、音楽が目覚める。
              </p>

              <ul className="space-y-2 lg:space-y-3">
                <li className="flex items-start gap-2 lg:gap-3 text-gray-400 text-xs lg:text-sm">
                  <i className="ri-checkbox-circle-fill text-neon-pink mt-0.5"></i>
                  <span>NFCタグ対応の物体を探す</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-3 text-gray-400 text-xs lg:text-sm">
                  <i className="ri-checkbox-circle-fill text-neon-pink mt-0.5"></i>
                  <span>スマホを近づけるだけ</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-3 text-gray-400 text-xs lg:text-sm">
                  <i className="ri-checkbox-circle-fill text-neon-pink mt-0.5"></i>
                  <span>物理的な接触が音を解放</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Step 2: Ignition */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-b from-neon-green/20 to-transparent rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-700" />
            <div className="relative bg-black/50 backdrop-blur-sm rounded-2xl lg:rounded-3xl p-6 lg:p-12 border border-white/10 hover:border-neon-green/50 transition-all duration-500 h-full">
              <div
                className="absolute -top-4 -left-4 lg:-top-6 lg:-left-6 w-10 h-10 lg:w-16 lg:h-16 rounded-full bg-neon-green flex items-center justify-center font-display text-xl lg:text-3xl text-black"
                style={{ boxShadow: '0 0 40px rgba(0, 255, 65, 0.6)' }}
              >
                02
              </div>

              <div
                className="w-14 h-14 lg:w-20 lg:h-20 mb-4 lg:mb-8 rounded-xl lg:rounded-2xl bg-gradient-to-br from-neon-green/20 to-transparent border-2 border-neon-green flex items-center justify-center mx-auto"
                style={{ boxShadow: '0 0 30px rgba(0, 255, 65, 0.3)' }}
              >
                <i className="ri-fire-line text-2xl lg:text-4xl text-neon-green"></i>
              </div>

              <h3 className="text-white text-xl lg:text-3xl font-bold mb-2 lg:mb-4 text-center">
                発火 / IGNITION
              </h3>

              <div
                className="w-2/5 h-0.5 bg-neon-green mx-auto mb-4 lg:mb-8"
                style={{ boxShadow: '0 0 15px #00ff41' }}
              />

              <p className="text-gray-300 text-xs lg:text-base leading-relaxed text-center mb-4 lg:mb-6">
                アンチグラビティな挙動で、瞬時にカード型UIが浮き上がる。剥き出しの音が鳴り響く。
              </p>

              <ul className="space-y-2 lg:space-y-3">
                <li className="flex items-start gap-2 lg:gap-3 text-gray-400 text-xs lg:text-sm">
                  <i className="ri-checkbox-circle-fill text-neon-green mt-0.5"></i>
                  <span>即座に音楽が再生開始</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-3 text-gray-400 text-xs lg:text-sm">
                  <i className="ri-checkbox-circle-fill text-neon-green mt-0.5"></i>
                  <span>浮遊するカードUI表示</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-3 text-gray-400 text-xs lg:text-sm">
                  <i className="ri-checkbox-circle-fill text-neon-green mt-0.5"></i>
                  <span>視覚的なフィードバック</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Step 3: Strata */}
          <div className="group relative">
            <div className="absolute inset-0 bg-gradient-to-b from-neon-blue/20 to-transparent rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-700" />
            <div className="relative bg-black/50 backdrop-blur-sm rounded-2xl lg:rounded-3xl p-6 lg:p-12 border border-white/10 hover:border-neon-blue/50 transition-all duration-500 h-full">
              <div
                className="absolute -top-4 -left-4 lg:-top-6 lg:-left-6 w-10 h-10 lg:w-16 lg:h-16 rounded-full bg-neon-blue flex items-center justify-center font-display text-xl lg:text-3xl text-black"
                style={{ boxShadow: '0 0 40px rgba(0, 217, 255, 0.6)' }}
              >
                03
              </div>

              <div
                className="w-14 h-14 lg:w-20 lg:h-20 mb-4 lg:mb-8 rounded-xl lg:rounded-2xl bg-gradient-to-br from-neon-blue/20 to-transparent border-2 border-neon-blue flex items-center justify-center mx-auto"
                style={{ boxShadow: '0 0 30px rgba(0, 217, 255, 0.3)' }}
              >
                <i className="ri-stack-line text-2xl lg:text-4xl text-neon-blue"></i>
              </div>

              <h3 className="text-white text-xl lg:text-3xl font-bold mb-2 lg:mb-4 text-center">
                深度 / DIVE
              </h3>

              <div
                className="w-2/5 h-0.5 bg-neon-blue mx-auto mb-4 lg:mb-8"
                style={{ boxShadow: '0 0 15px #00d9ff' }}
              />

              <p className="text-gray-300 text-xs lg:text-base leading-relaxed text-center mb-4 lg:mb-6">
                その音が気に入れば、さらに深く「層」へと潜り込む。すべてが明らかになる。
              </p>

              <ul className="space-y-2 lg:space-y-3">
                <li className="flex items-start gap-2 lg:gap-3 text-gray-400 text-xs lg:text-sm">
                  <i className="ri-checkbox-circle-fill text-neon-blue mt-0.5"></i>
                  <span>アーティストプロフィール</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-3 text-gray-400 text-xs lg:text-sm">
                  <i className="ri-checkbox-circle-fill text-neon-blue mt-0.5"></i>
                  <span>ライブ情報・場所</span>
                </li>
                <li className="flex items-start gap-2 lg:gap-3 text-gray-400 text-xs lg:text-sm">
                  <i className="ri-checkbox-circle-fill text-neon-blue mt-0.5"></i>
                  <span>SNS・音源プラットフォーム</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
